/**
 * The web app's copy of the shared date helpers.
 *
 * `packages/shared` is a Node package built with `tsc`; the browser build does
 * not consume it. The rule it encodes matters more than the duplication: a
 * calendar date is a local date, never one derived from `toISOString()`, or
 * the client and the server disagree about what day it is for the hours
 * between local midnight and UTC midnight.
 *
 * Keep this in step with `packages/shared/src/dates.ts`.
 */

/**
 * The household's zone, once /auth/me has said what it is. Until then, and
 * on a public page, the browser's own zone: right for a kitchen, and the
 * only answer available before signing in.
 */
let householdZone: string | undefined
let formatter: Intl.DateTimeFormat | undefined

export function setHouseholdTimeZone(zone: string | null | undefined): void {
  householdZone = zone || undefined
  formatter = undefined
  if (householdZone) {
    try {
      formatter = new Intl.DateTimeFormat('en-CA', { timeZone: householdZone, year: 'numeric', month: '2-digit', day: '2-digit' })
    } catch {
      householdZone = undefined
    }
  }
}

export function toDateString(date: Date = new Date()): string {
  if (formatter) return formatter.format(date)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function today(): string {
  return toDateString()
}

export function fromDateString(value: string): Date {
  const [year, month, day] = value.split('-').map(Number)
  return new Date(year!, (month ?? 1) - 1, day ?? 1)
}

export function addDays(value: string, days: number): string {
  const date = fromDateString(value)
  date.setDate(date.getDate() + days)
  return toDateString(date)
}

export function weekStartFor(value: string): string {
  const date = fromDateString(value)
  const isoDay = date.getDay() === 0 ? 7 : date.getDay()
  date.setDate(date.getDate() - (isoDay - 1))
  return toDateString(date)
}

/**
 * How a best-before date reads on a badge, from a number of days.
 *
 * One helper rather than two copies, because the two screens that show this
 * had drifted: the kitchen list said "today" and "tomorrow" where *Use it up*
 * still counted in days, and both said "1 days past".
 *
 * Null means the item has no best-before date at all, which is not the same
 * as being in date and should show no badge.
 */
export function bestBeforeLabel(days: number | null | undefined): string | null {
  if (days === null || days === undefined) return null
  if (days < 0) {
    const over = Math.abs(days)
    return `${over} ${over === 1 ? 'day' : 'days'} past`
  }
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  return `${days} days`
}
